import React, { FC, PropsWithChildren } from 'react';
import classNames from 'classnames';

import styles from './StatusIcon.module.scss';

const sectionId = 'StatusIcon';

interface StatusIconChildClasses {
  iconSkin?: string;
}

interface StatusIconProps {
  color?: 'stationary' | 'success' | 'info' | 'error' | 'warning';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  border?: boolean;
  background?: boolean;
  className?: string;
  classes?: StatusIconChildClasses;
}

/**
 *
 * @param color - change icon color for border and/or background
 * @param size - change icon size
 * @param className - augment styles
 * @param border - show a border
 * @param background - show a background fill color
 * @param children - children will show up inside the icon
 * @param rest - data-testid or other react/html supported attributes
 */
const StatusIcon: FC<PropsWithChildren<StatusIconProps>> = ({
  color= 'stationary',
  size = 'md',
  className,
  border = false,
  background = true,
  children,
  classes,
  ...rest
}) => (
  <div
    data-testid={sectionId}
    {...rest}
    className={classNames({
      // border/background variations
      [styles['has_border']]: border,
      [styles['has_background']]: background,

      // color variations
      [styles['color_stationary']]: color === 'stationary',
      [styles['color_success']]: color === 'success',
      [styles['color_info']]: color === 'info',
      [styles['color_error']]: color === 'error',
      [styles['color_warning']]: color === 'warning',
      // size variations
      [styles['size_xs']]: size === 'xs',
      [styles['size_sm']]: size === 'sm',
      [styles['size_md']]: size === 'md',
      [styles['size_lg']]: size === 'lg',
      [styles['size_xl']]: size === 'xl',
    }, className)}
  >
    <div className={classNames(styles['icon_skin'], classes?.iconSkin)}>
      {children}
    </div>
  </div>
);

export type { StatusIconProps };
export { sectionId, StatusIcon };
